import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import { Loader2 } from "lucide-react";
import type { ReportFilters } from "@/features/reports/types";
import { useLowStockReport } from "@/features/spares/reportsApi";

export const Route = createFileRoute("/_authenticated/reports/low-stock-spares")({
  component: LowStockSparesReport,
});

function LowStockSparesReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [search, setSearch] = useState("");
  const [storeLocation, setStoreLocation] = useState("");
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;

  const { data, isLoading } = useLowStockReport({
    branchId: effectiveBranchId,
    departmentId: filters.departmentId ?? null,
    categoryId: filters.categoryId ?? null,
    storeLocation: storeLocation || null,
    search: search || null,
  });
  const rows = data ?? [];

  const exportRows = useMemo(() => () => rows.map((r) => ({
    "Spare Code": r.spare_code,
    "Spare Name": r.spare_name,
    Branch: r.branch_name,
    "Department / Store": [r.department_name, r.store_location].filter(Boolean).join(" / "),
    "Available Qty": r.available,
    "Reorder Level": r.reorder_level,
    "Minimum Stock": r.min_stock,
    "Shortage Qty": r.shortage,
    "Suggested Reorder": r.suggested_qty,
    "Last Movement": r.last_movement_at ?? "",
  })), [rows]);

  return (
    <ReportShell
      id="R-40" title="Low Stock / Reorder Report"
      description="Spares below reorder / minimum stock level."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "departmentId", "categoryId"]}
      exportRows={exportRows}
    >
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1">
            <Label className="text-xs">Store Location</Label>
            <Input value={storeLocation} onChange={(e) => setStoreLocation(e.target.value)} placeholder="Any store" />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Search</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Code / name" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Spare</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Dept / Store</TableHead>
                <TableHead className="text-right">Available</TableHead>
                <TableHead className="text-right">Reorder</TableHead>
                <TableHead className="text-right">Min</TableHead>
                <TableHead className="text-right">Shortage</TableHead>
                <TableHead className="text-right">Suggested</TableHead>
                <TableHead>Last Movement</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.spare_id}-${r.branch_id}-${r.store_location}`}>
                    <TableCell className="font-mono text-xs">{r.spare_code}</TableCell>
                    <TableCell className="font-medium">{r.spare_name}</TableCell>
                    <TableCell>{r.branch_name}</TableCell>
                    <TableCell className="text-xs">{r.department_name} / {r.store_location}</TableCell>
                    <TableCell className="text-right tabular-nums"><Badge variant={r.available === 0 ? "destructive" : "secondary"}>{r.available}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{r.reorder_level}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.min_stock}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.shortage}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.suggested_qty}</TableCell>
                    <TableCell className="text-xs">{r.last_movement_at?.slice(0, 10) ?? "—"}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="py-8 text-center text-sm text-muted-foreground">No spare records found for the selected filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">Showing {rows.length} low-stock entries.</div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
